import React from "react"

import { supportsAudioFilter, type MetricRecord } from "@/metrics/runtime"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { AudioFilterConfig } from "@/types/metrics/metadata"

import {
  AudioFilterDetails,
  AudioFilterToggle,
} from "../../configuration/audio-filter"
import { requiresSingleSpeaker } from "../../utils/directoryAudioScope"

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TypeScript mixin constructors require any[] rest params
type Constructor<T = object> = new (...args: any[]) => T

/**
 * Mixin that appends the shared audio-filter editor to the record's editor metadata.
 *
 * Subclasses that override `renderEditorMetadata` must call
 * `super.renderEditorMetadata(...)` to keep the audio-filter appended.
 */
export function WithAudioFilterMixin<T extends Constructor<MetricRecord>>(
  Base: T,
) {
  return class extends Base {
    renderEditorMetadata(
      metricRecordData: MetricRecordData,
      onChange: (metric: MetricRecordData) => void,
      textAreaRef?: React.RefObject<HTMLTextAreaElement>,
      editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
    ): React.ReactNode {
      const baseNode = super.renderEditorMetadata(
        metricRecordData,
        onChange,
        textAreaRef,
        editorContext,
      )

      if (!supportsAudioFilter(metricRecordData)) {
        return baseNode
      }

      const audioFilter = metricRecordData.metric_metadata?.audio_filter
      if (editorContext?.readOnly && audioFilter === undefined) {
        return baseNode
      }
      const handleAudioFilterChange = (next: AudioFilterConfig | undefined) => {
        const { audio_filter: _prev, ...rest } =
          metricRecordData.metric_metadata ?? {}
        onChange({
          ...metricRecordData,
          metric_metadata:
            next === undefined ? rest : { ...rest, audio_filter: next },
        })
      }

      return (
        <div className="flex flex-col gap-4">
          {baseNode}
          <AudioFilterToggle
            value={audioFilter}
            onChange={handleAudioFilterChange}
          />
          <AudioFilterDetails
            value={audioFilter}
            onChange={handleAudioFilterChange}
            metricManager={metricRecordData.metric_manager}
            lockToSingleSpeaker={requiresSingleSpeaker(
              editorContext?.metricDirectoryEntry,
            )}
          />
        </div>
      )
    }
  }
}
